import { MaybeRefOrGetter, nextTick, onMounted, onScopeDispose, ref, toValue, watch } from 'vue';

export interface UseExpandableTextOptions {
    disableExpansion?: boolean;
    animationDuration?: number;
    collapseText?: (expandedText: string) => string;
}

const MEASURED_STYLE_PROPS = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'fontVariant',
    'letterSpacing',
    'wordSpacing',
    'textTransform',
    'paddingLeft',
    'paddingRight',
    'paddingTop',
    'paddingBottom',
    'borderLeftWidth',
    'borderRightWidth',
    'borderLeftStyle',
    'borderRightStyle',
    'boxSizing',
] as const;

export function useExpandableText(expandedText: MaybeRefOrGetter<string>, options: UseExpandableTextOptions = {}) {
    const { animationDuration = 200, collapseText, disableExpansion } = options;

    const resolveCollapsed = (): string => {
        const expanded = toValue(expandedText);
        return collapseText ? collapseText(expanded) : expanded.charAt(0);
    };

    const currentText = ref(resolveCollapsed());
    const width = ref(0);
    const expanded = ref(false);
    const container = ref<HTMLElement | null>(null);
    const maskActive = ref(false);
    const isExpanded = ref(false);

    let swapTimer: ReturnType<typeof setTimeout> | undefined;
    let measureEl: HTMLSpanElement | undefined;

    function ensureMeasureEl(): HTMLSpanElement {
        if (!measureEl) {
            const el = document.createElement('span');
            el.setAttribute('aria-hidden', 'true');
            el.style.position = 'absolute';
            el.style.top = '-9999px';
            el.style.left = '-9999px';
            el.style.visibility = 'hidden';
            el.style.whiteSpace = 'nowrap';
            el.style.pointerEvents = 'none';
            document.body.appendChild(el);
            measureEl = el;
        }
        return measureEl;
    }

    function measureTextWidth(text: string): number {
        if (!container.value) {
            return width.value;
        }
        const el = ensureMeasureEl();
        const styles = getComputedStyle(container.value);
        for (const prop of MEASURED_STYLE_PROPS) {
            el.style[prop] = styles[prop];
        }
        el.textContent = text;
        return el.offsetWidth;
    }

    async function expand() {
        if (disableExpansion) return;
        clearTimeout(swapTimer);
        expanded.value = true;

        const newText = toValue(expandedText);
        const expandedWidth = measureTextWidth(newText);

        currentText.value = newText;
        width.value = expandedWidth;
        maskActive.value = true;
        isExpanded.value = false; // parked at start

        await nextTick();
        if (!expanded.value) return;
        isExpanded.value = true; // sweep to 100%
    }

    function collapse() {
        if (disableExpansion) return;
        clearTimeout(swapTimer);
        expanded.value = false;

        isExpanded.value = false; // retract
        width.value = measureTextWidth(resolveCollapsed());

        swapTimer = setTimeout(() => {
            currentText.value = resolveCollapsed();
            maskActive.value = false;
        }, animationDuration);
    }

    watch(resolveCollapsed, (value) => {
        if (!expanded.value) {
            currentText.value = value;
            width.value = measureTextWidth(value);
        }
    });

    onMounted(() => {
        width.value = measureTextWidth(currentText.value);
    });

    onScopeDispose(() => {
        clearTimeout(swapTimer);
        measureEl?.remove();
    });

    return {
        currentText,
        width,
        maskActive,
        isExpanded,
        container,
        animationDuration,
        expand,
        collapse,
    };
}
